import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import { forgeApi } from '../../services/fileShareApi';
import { applyDefaultMeasureUnits } from './forgeMeasureUnits';

const ForgeViewer = ({ fileId, fileName, fileType, onError }) => {
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerConfig, setViewerConfig] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initializeViewer = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get viewer configuration from backend
        const configResponse = await forgeApi.getViewerConfig(fileId);
        const config = configResponse.data;

        if (!isMounted) return;

        setViewerConfig(config);

        // Load Forge Viewer script if not already loaded
        if (!window.Autodesk) {
          await loadForgeViewerScript();
        }

        if (!isMounted) return;

        // Initialize viewer
        await initializeForgeViewer(config);

      } catch (err) {
        console.error('Error initializing Forge viewer:', err);
        if (isMounted) {
          setError('Failed to initialize 3D viewer');
          if (onError) onError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeViewer();

    return () => {
      isMounted = false;
      if (viewer) {
        if (typeof viewer.__asrosyncMeasureUnitsCleanup === 'function') {
          viewer.__asrosyncMeasureUnitsCleanup();
        }
        viewer.finish();
        setViewer(null);
      }
    };
  }, [fileId]);

  // Keep the canvas in sync with its container (comments panel collapse, window resize)
  useEffect(() => {
    const container = viewerRef.current;
    if (!viewer || !container || typeof window.ResizeObserver !== 'function') return undefined;

    let frame = null;
    const observer = new window.ResizeObserver(() => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        try {
          if (typeof viewer.resize === 'function') {
            viewer.resize();
          }
        } catch (err) {
          console.warn('Forge viewer resize after layout change:', err);
        }
      });
    });
    observer.observe(container);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [viewer]);

  const loadForgeViewerScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Autodesk) {
        resolve();
        return;
      }

      // Load CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';
      document.head.appendChild(link);

      // Load JavaScript
      const script = document.createElement('script');
      script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
      script.onload = () => {
        console.log('Forge Viewer script loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('Failed to load Forge Viewer script');
        reject(new Error('Failed to load Forge Viewer script'));
      };
      document.head.appendChild(script);
    });
  };

  const initializeForgeViewer = async (config) => {
    return new Promise((resolve, reject) => {
      try {
        const options = {
          env: 'AutodeskProduction',
          api: 'derivativeV2',
          getAccessToken: (onTokenReady) => {
            onTokenReady(config.accessToken, 3600); // 1 hour
          }
        };

        // eslint-disable-next-line no-undef
        Autodesk.Viewing.Initializer(options, () => {
          const viewerContainer = viewerRef.current;
          if (!viewerContainer) {
            reject(new Error('Viewer container not found'));
            return;
          }

          // eslint-disable-next-line no-undef
          const viewerInstance = new Autodesk.Viewing.GuiViewer3D(viewerContainer);
          
          // Configure viewer
          viewerInstance.start();
          
          // Load the model
          const documentId = `urn:${config.forgeUrn}`;
          // eslint-disable-next-line no-undef
          Autodesk.Viewing.Document.load(documentId, (doc) => {
            const viewables = doc.getRoot().getDefaultGeometry();
            if (viewables) {
              viewerInstance.loadDocumentNode(doc, viewables).then(() => {
                console.log('Model loaded successfully');
                // Client default: feet + fractional inches (e.g. 6' - 8")
                applyDefaultMeasureUnits(viewerInstance);
                setViewer(viewerInstance);
                resolve();
              });
            } else {
              reject(new Error('No viewable geometry found'));
            }
          }, (error) => {
            console.error('Error loading document:', error);
            reject(error);
          });
        });
      } catch (err) {
        console.error('Error initializing viewer:', err);
        reject(err);
      }
    });
  };

  const handleFullscreen = () => {
    if (viewer && viewerRef.current) {
      if (viewerRef.current.requestFullscreen) {
        viewerRef.current.requestFullscreen();
      } else if (viewerRef.current.webkitRequestFullscreen) {
        viewerRef.current.webkitRequestFullscreen();
      } else if (viewerRef.current.msRequestFullscreen) {
        viewerRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <Box height="100%" position="relative">
      {/* Viewer Container - Always rendered */}
      <Box
        ref={viewerRef}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      />
      
      {/* Loading Overlay */}
      {loading && (
        <Box 
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          bgcolor="rgba(255, 255, 255, 0.9)"
          zIndex={2000}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading 3D viewer...
          </Typography>
        </Box>
      )}

      {/* Error Overlay */}
      {error && (
        <Box 
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.9)"
          zIndex={2000}
          p={2}
        >
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </Box>
      )}
      
      {/* Viewer Controls */}
      {!loading && !error && (
        <Box
          position="absolute"
          top={8}
          right={8}
          display="flex"
          gap={1}
          zIndex={1000}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleFullscreen}
            sx={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)'
              }
            }}
          >
            Fullscreen
          </Button>
        </Box>
      )}

      {/* File Info */}
      {!loading && !error && (
        <Box
          position="absolute"
          bottom={8}
          left={8}
          backgroundColor="rgba(0, 0, 0, 0.7)"
          color="white"
          padding={1}
          borderRadius={1}
          zIndex={1000}
        >
          <Typography variant="caption" display="block">
            {fileName} ({fileType})
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ForgeViewer;
